import { createExternalStore } from "@client/utils/createExternalStore";

/**
 * Whether the download engine is in a state where it can actually run the
 * queue — i.e. whether it holds the native downloader listeners that carry
 * every progress, completion and failure event.
 *
 * This exists because the failure it reports used to be completely invisible.
 * The engine marked itself started before attaching those listeners, and
 * `tick()` is reachable without going through startup (the "Download all"
 * toggle calls `syncWithCatalog` directly), so a startup that never got as far
 * as the listeners still handed the whole round to the iOS background session
 * — which downloaded it perfectly while the app heard nothing back. Every
 * video sat under "Queued" forever, no error was ever shown (failures ride the
 * same listener), and neither relaunching nor wiping and re-enabling the round
 * recovered it, because both re-ran the identical sequence. The engine now
 * refuses to hand anything off until the listeners are attached; if they can't
 * be, that has to be SAID rather than presented as a queue that will never
 * move.
 *
 * Deliberately outside `mobileVideoStore`: this is ephemeral per-launch state,
 * and the store is persisted (same reasoning as `downloadProgress.ts`).
 */
export type DownloadEngineStatus =
  /** Not started yet — the engine boots with the funder workspace. */
  | { state: "idle" }
  /** Listeners attached; handoffs are allowed and their events will land. */
  | { state: "ready" }
  /** The engine cannot receive download events, so it will not start one. */
  | { state: "unavailable"; message: string };

export const downloadEngineStatusStore =
  createExternalStore<DownloadEngineStatus>({ state: "idle" });

/** Shown on the Downloads card when the engine can't run the queue at all. */
export const downloadEngineUnavailableMessage =
  "Downloads can't start on this device right now. Reopening the app usually fixes it.";
